<template>
  <div class="rd-screen">
    <report-tools :copy="sections.length > 0" @copy="copyTable"></report-tools>

    <report-state
      v-if="!selectedCompanies.length"
      icon="mdi-domain"
      :title="$t('no_companies_selected')"
    ></report-state>
    <report-state v-else-if="isLoading" kind="loading"></report-state>
    <report-state
      v-else-if="!sections.length"
      icon="mdi-food-off"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <template v-else>
      <section v-for="section in sections" :key="section.key" class="mr-company">
        <div class="rd-section mr-company__head">
          <div class="mr-company__title">
            <h2>{{ section.name }}</h2>
            <p v-if="section.details.length" class="mr-company__details">
              <span v-for="detail in section.details" :key="detail.icon" class="mr-company__detail">
                <v-icon size="14" color="#8a817a">{{ detail.icon }}</v-icon>
                {{ detail.text }}
              </span>
            </p>
          </div>
          <span class="rd-num">{{ section.summary }}</span>
          <button
            v-if="isSingleDay && !packing"
            type="button"
            class="rd-btn rd-btn--sm rd-btn--pri"
            @click="openAddDialog(section.company)"
          >
            <v-icon size="16" color="#ffffff">mdi-plus</v-icon>
            {{ $t("add_meals2") }}
          </button>
        </div>

        <div class="rd-card">
          <table class="rd-tbl">
            <thead>
              <tr>
                <th scope="col" class="rd-col-num">#</th>
                <th scope="col">{{ $t("meal") }}</th>
                <th v-if="showCombination" scope="col">{{ $t("more_restaurants_report.contents") }}</th>
                <th v-for="shift in visibleShifts" :key="shift.id" scope="col" class="is-right">
                  <report-shift-tag :shift="shift.id"></report-shift-tag>
                </th>
                <th scope="col" class="is-right">{{ $t("total") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in section.rows" :key="row.key">
                <td class="rd-num rd-muted">{{ index + 1 }}</td>
                <td class="is-name" :title="row.title">{{ row.name }}</td>
                <td v-if="showCombination" class="rd-muted mr-items__combo">{{ row.combination }}</td>
                <td v-for="shift in visibleShifts" :key="shift.id" class="is-right rd-num">
                  <span :class="{ 'is-zero': !row[shift.key].ordered }">
                    <b>{{ row[shift.key].ordered }}</b>
                    <span class="mr-taken" :class="takenClass(row[shift.key])"> / {{ row[shift.key].taken }}</span>
                  </span>
                </td>
                <td class="is-right rd-num">
                  <b>{{ row.total }}</b>
                  <span class="mr-taken" :class="takenClass({ ordered: row.total, taken: row.totalPayed })"> / {{ row.totalPayed }}</span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td></td>
                <td>{{ $t("total") }}</td>
                <td v-if="showCombination"></td>
                <td v-for="shift in visibleShifts" :key="shift.id" class="is-right rd-num">
                  {{ section.totals[shift.key].ordered }}
                  <span class="mr-taken"> / {{ section.totals[shift.key].taken }}</span>
                </td>
                <td class="is-right rd-num">
                  {{ section.totals.total }}
                  <span class="mr-taken"> / {{ section.totals.totalPayed }}</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </template>

    <report-meals-packages-add-meal
      v-if="selectedCompany"
      v-model="addMealDialog"
      :company="selectedCompany"
      :date="filters.dates.from"
      @refreshMeal="loadMealsForRestaurantCompanies"
    ></report-meals-packages-add-meal>
  </div>
</template>

<script>
import ReportMealsTabPackages from "@/components/report/more-restaurants/tabs/ReportMealsTabPackages.vue";
import hasShiftBinding from "../../../shared/hasShiftBinding";
import hasShiftSubtitle from "../../../shared/hasShiftSubtitle";
import hasMorePackageSections from "../../../shared/more/hasMorePackageSections";
import ReportShiftTag from "../../../shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "../../copyTable";
import ReportState from "../../ReportState.vue";
import ReportTools from "../../ReportTools.vue";

/**
 * Pakovanje / Preuzeti obroci firmi "više restorana" na računaru: po firmi obroci (i sadržaj paketa kad ga ima) po
 * smenama kao poručeno / preuzeto, sa zbirom. Za jedan dan se u Preuzetim obrocima uz svaku firmu mogu dodati obroci
 * (u Pakovanju je to dugme "Obrok" u zaglavlju stranice). Firme, smene i
 * pretragu čita iz zajedničke trake. Nasleđuje učitavanje, štampu i Excel iz starog taba; prop `packing` bira
 * Pakovanje (paket sa različitim izborima je poseban red) umesto Preuzetih obroka.
 */
export default {
  name: "ReportDesktopMorePackages",
  extends: ReportMealsTabPackages,
  mixins: [hasShiftBinding, hasShiftSubtitle, hasMorePackageSections],
  components: { ReportShiftTag, ReportState, ReportTools },
  computed: {
    emptyText() {
      return this.filters.search ? "" : this.$t("no_meals_for_selected_period");
    },
    kpis() {
      return [
        { key: "ordered", label: this.$t("ordered"), value: this.totals.ordered, tone: "inverse" },
        { key: "taken", label: this.$t("taken"), value: this.totals.taken },
        { key: "companies", label: this.$t("companies"), value: this.sections.length },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.sections.some((section) => section.rows.length) ? kpis : []);
      },
      immediate: true,
    },
    isLoading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    async copyTable() {
      const headers = [
        this.$t("companies"),
        this.$t("meal"),
        ...(this.showCombination ? [this.$t("more_restaurants_report.contents")] : []),
        ...this.visibleShifts.map((shift) => this.$t(shift.label)),
        this.$t("total"),
      ];
      const rows = this.sections.flatMap((section) =>
        section.rows.map((row) => [
          section.name,
          row.name,
          ...(this.showCombination ? [row.combination] : []),
          ...this.visibleShifts.map((shift) => `${row[shift.key].ordered} / ${row[shift.key].taken}`),
          `${row.total} / ${row.totalPayed}`,
        ])
      );
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
